class MexicoArtSpotlightGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.mainLink = this.querySelector('[data-ma-spotlight-main-link]');
    this.mainImage = this.mainLink?.querySelector('img');
    this.thumbButtons = [...this.querySelectorAll('[data-ma-spotlight-thumb]')];
    this.sources = [...this.querySelectorAll('[data-ma-spotlight-source]')].map((source) => ({
      index: Number(source.dataset.index),
      alt: source.dataset.alt,
      mainSrc: source.dataset.mainSrc,
      mainSrcset: source.dataset.mainSrcset,
      thumbSrc: source.dataset.thumbSrc,
      thumbSrcset: source.dataset.thumbSrcset,
      width: source.dataset.width,
      height: source.dataset.height,
    }));

    if (!this.mainLink || !this.mainImage || this.sources.length <= 1) return;

    this.currentIndex = 0;
    this.intervalMs = Number(this.dataset.interval) || 4800;
    this.thumbLabel = this.dataset.thumbLabel || 'Show image';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handlePointerEnter = () => this.stopRotation();
    this.handlePointerLeave = () => this.startRotation();
    this.handleFocusIn = () => this.stopRotation();
    this.handleFocusOut = () => this.startRotation();
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.stopRotation();
      } else {
        this.startRotation();
      }
    };

    this.thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const targetIndex = Number(button.dataset.targetIndex);
        if (Number.isNaN(targetIndex)) return;

        this.showIndex(targetIndex);
      });
    });

    this.addEventListener('mouseenter', this.handlePointerEnter);
    this.addEventListener('mouseleave', this.handlePointerLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.render();
    this.startRotation();
  }

  disconnectedCallback() {
    this.stopRotation();
    this.removeEventListener('mouseenter', this.handlePointerEnter);
    this.removeEventListener('mouseleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  startRotation() {
    if (this.prefersReducedMotion.matches || this.sources.length <= 1 || document.hidden) return;

    this.stopRotation();
    this.rotationTimer = window.setInterval(() => {
      this.showIndex((this.currentIndex + 1) % this.sources.length);
    }, this.intervalMs);
  }

  stopRotation() {
    if (!this.rotationTimer) return;

    window.clearInterval(this.rotationTimer);
    this.rotationTimer = null;
  }

  showIndex(index) {
    if (index === this.currentIndex || !this.sources[index]) return;

    this.currentIndex = index;
    this.render();
    this.startRotation();
  }

  render() {
    const activeSource = this.sources[this.currentIndex];
    const secondarySources = this.sources.filter((source) => source.index !== activeSource.index);

    this.classList.add('is-swapping');
    window.clearTimeout(this.swapTimer);
    this.swapTimer = window.setTimeout(() => {
      this.classList.remove('is-swapping');
    }, 220);

    this.applySourceToImage(this.mainImage, activeSource, 'main');

    this.thumbButtons.forEach((button, slotIndex) => {
      const source = secondarySources[slotIndex];
      if (!source) {
        button.hidden = true;
        return;
      }

      button.hidden = false;
      button.dataset.targetIndex = String(source.index);
      button.setAttribute('aria-label', `${this.thumbLabel} ${slotIndex + 1}`);

      const image = button.querySelector('img');
      if (image) this.applySourceToImage(image, source, 'thumb');
    });
  }

  applySourceToImage(image, source, mode) {
    if (!image || !source) return;

    image.src = mode === 'main' ? source.mainSrc : source.thumbSrc;
    image.srcset = mode === 'main' ? source.mainSrcset : source.thumbSrcset;
    image.alt = source.alt;

    if (source.width) image.width = Number(source.width);
    if (source.height) image.height = Number(source.height);
  }
}

if (!customElements.get('ma-spotlight-gallery')) {
  customElements.define('ma-spotlight-gallery', MexicoArtSpotlightGallery);
}
